import { Modal } from 'core/components/ui/Modal'
import axiosInstance from 'core/network/axiosInstance'
import React, { useEffect, useState } from 'react'
import { CatalogItem } from '../../domain/entities/Catalogue'

interface SkuFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: Omit<CatalogItem, 'id' | 'addedBy' | 'isDeleted' | 'createdAt' | 'updatedAt'>) => Promise<void>
  item?: CatalogItem | null
}

const UNIT_OPTIONS = ['Bag', 'Kg', 'Litre', 'Bottle', 'Packet', 'Piece', 'Box', 'Can']

// Helpers to restrict inputs strictly to digits / numbers and prevent any symbols (+, -, e, etc.) or alphabets
const sanitizeInteger = (value: string): string => {
  return value.replace(/\D/g, '')
}

const sanitizePrice = (value: string): string => {
  let cleaned = value.replace(/[^0-9.]/g, '')
  const parts = cleaned.split('.')
  if (parts.length > 2) {
    cleaned = `${parts[0]}.${parts.slice(1).join('')}`
  }

  return cleaned
}

const handleIntegerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) {
    return
  }

  if (e.ctrlKey || e.metaKey) {
    return
  }

  // Block any non-digit character (e.g. -, +, e, E, .)
  if (!/^\d$/.test(e.key)) {
    e.preventDefault()
  }
}

const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) {
    return
  }

  if (e.ctrlKey || e.metaKey) {
    return
  }

  if (e.key === '.') {
    if ((e.currentTarget as HTMLInputElement).value.includes('.')) {
      e.preventDefault()
    }

    return
  }

  // Block any non-digit character (e.g. -, +, e, E)
  if (!/^\d$/.test(e.key)) {
    e.preventDefault()
  }
}

const handlePaste = (
  e: React.ClipboardEvent<HTMLInputElement>,
  sanitizer: (val: string) => string,
  updater: (val: string) => void,
) => {
  e.preventDefault()
  const pasted = e.clipboardData.getData('text')
  const clean = sanitizer(pasted)
  const input = e.currentTarget
  const start = input.selectionStart ?? 0
  const end = input.selectionEnd ?? 0
  const current = input.value
  const result = sanitizer(current.slice(0, start) + clean + current.slice(end))
  updater(result)
}

const getInitialFormState = (item: CatalogItem | null | undefined) => {
  if (item) {
    return {
      name: item.name,
      description: item.description || '',
      images: item.images && item.images.length > 0 ? [...item.images] : [],
      moq: String(item.moq),
      mrp: String(item.mrp),
      sellingPrice: String(item.sellingPrice),
      manufacturerPrice: String(item.manufacturerPrice || '0'),
      stock: String(item.stock),
      variant: item.variant || '',
      unit: item.unit || 'Bag',
      packWeightKg:
        item.unit === 'Kg' || item.packWeightKg === undefined || item.packWeightKg === null
          ? ''
          : String(item.packWeightKg),
    }
  }

  return {
    name: '',
    description: '',
    images: [],
    moq: '',
    mrp: '',
    sellingPrice: '',
    manufacturerPrice: '',
    stock: '',
    variant: '',
    unit: 'Bag',
    packWeightKg: '',
  }
}

interface SkuFormValidationValues {
  name: string
  description: string
  images: string[]
  mrp: string
  sellingPrice: string
  manufacturerPrice: string
  moq: string
  variant: string
  packWeightKg: string
  stock: string
}

const validateMrp = (mrp: string): string | undefined => {
  const numMrp = Number(mrp)
  if (!mrp.trim() || isNaN(numMrp) || numMrp <= 0) {
    return 'MRP must be greater than 0.'
  }

  return undefined
}

const validateSellingPrice = (sellingPrice: string, mrp: string): string | undefined => {
  const numSelling = Number(sellingPrice)
  const numMrp = Number(mrp)
  if (!sellingPrice.trim() || isNaN(numSelling) || numSelling <= 0) {
    return 'Selling Price must be greater than 0.'
  }

  if (mrp.trim() && numMrp > 0 && numSelling > numMrp) {
    return 'Selling Price cannot be greater than MRP.'
  }

  return undefined
}

const validateManufacturerPrice = (manufacturerPrice: string): string | undefined => {
  const numMfg = Number(manufacturerPrice)
  if (!manufacturerPrice.trim() || isNaN(numMfg)) {
    return 'Manufacturer Price is required.'
  }

  if (numMfg < 0) {
    return 'Manufacturer Price must be 0 or greater.'
  }

  return undefined
}

const validateBasicFields = (name: string, description: string, images: string[]): Record<string, string> => {
  const errors: Record<string, string> = {}
  if (!name.trim()) {
    errors.name = 'Product Name is required.'
  }

  if (!description.trim()) {
    errors.description = 'Product Description is required.'
  }

  if (images.length === 0) {
    errors.images = 'At least one product image is required.'
  }

  return errors
}

const validatePricingFields = (
  mrp: string,
  sellingPrice: string,
  manufacturerPrice: string,
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const mrpError = validateMrp(mrp)
  if (mrpError) {
    errors.mrp = mrpError
  }

  const sellingError = validateSellingPrice(sellingPrice, mrp)
  if (sellingError) {
    errors.sellingPrice = sellingError
  }

  const mfgError = validateManufacturerPrice(manufacturerPrice)
  if (mfgError) {
    errors.manufacturerPrice = mfgError
  }

  return errors
}

const validateStockAndVariantFields = (
  moq: string,
  variant: string,
  packWeightKg: string,
  stock: string,
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const numMoq = Number(moq)
  const numPackWeightKg = Number(packWeightKg)
  const numStock = Number(stock)

  if (moq.trim() && (isNaN(numMoq) || numMoq < 0)) {
    errors.moq = 'Min Order Qty must be 0 or greater.'
  }

  if (!variant.trim()) {
    errors.variant = 'Size / Packaging is required.'
  }

  if (packWeightKg.trim() && (isNaN(numPackWeightKg) || numPackWeightKg < 0)) {
    errors.packWeightKg = 'Pack weight must be 0 or greater.'
  }

  if (stock.trim() && (isNaN(numStock) || numStock < 0)) {
    errors.stock = 'Stock quantity must be 0 or greater.'
  }

  return errors
}

const validateSkuForm = (values: SkuFormValidationValues): Record<string, string> => {
  return {
    ...validateBasicFields(values.name, values.description, values.images),
    ...validatePricingFields(values.mrp, values.sellingPrice, values.manufacturerPrice),
    ...validateStockAndVariantFields(values.moq, values.variant, values.packWeightKg, values.stock),
  }
}

const updateErrorsForMrp = (
  mrpVal: string,
  sellingPriceVal: string,
  currentErrors: Record<string, string>,
): Record<string, string> => {
  const next = { ...currentErrors }
  delete next.mrp

  const numMrp = Number(mrpVal)
  const numSelling = Number(sellingPriceVal)
  const isOverMrp = sellingPriceVal.trim() && numMrp > 0 && numSelling > numMrp

  if (isOverMrp) {
    next.sellingPrice = 'Selling Price cannot be greater than MRP.'
    return next
  }

  if (next.sellingPrice === 'Selling Price cannot be greater than MRP.') {
    delete next.sellingPrice
  }

  return next
}

const updateErrorsForSellingPrice = (
  sellingPriceVal: string,
  mrpVal: string,
  currentErrors: Record<string, string>,
): Record<string, string> => {
  const next = { ...currentErrors }
  delete next.sellingPrice

  const numMrp = Number(mrpVal)
  const numSelling = Number(sellingPriceVal)
  if (mrpVal.trim() && numMrp > 0 && numSelling > numMrp) {
    next.sellingPrice = 'Selling Price cannot be greater than MRP.'
  }

  return next
}

const uploadCatalogFiles = async (files: FileList, maxSlots: number): Promise<string[]> => {
  const uploadedUrls: string[] = []
  const count = Math.min(files.length, maxSlots)

  for (let i = 0; i < count; i++) {
    const file = files[i]

    const { data: presigned } = await axiosInstance.post('/api/v1/storage/presigned-url', {
      file_name: file.name,
      content_type: file.type,
      category: 'catalog',
    })

    const uploadRes = await fetch(presigned.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    })

    if (!uploadRes.ok) {
      throw new Error(`Failed to upload ${file.name} to storage. Status: ${uploadRes.status}`)
    }

    uploadedUrls.push(presigned.public_url)
  }

  return uploadedUrls
}

const clearKey = (obj: Record<string, string>, key: string): Record<string, string> => {
  if (!(key in obj)) return obj
  const next = { ...obj }
  delete next[key]
  return next
}

interface SkuFormState {
  name: string
  description: string
  images: string[]
  moq: string
  mrp: string
  sellingPrice: string
  manufacturerPrice: string
  stock: string
  variant: string
  unit: string
  packWeightKg: string
  submitting: boolean
  uploadingImages: boolean
  errorMsg: string
  errors: Record<string, string>
}

interface SkuFormHandlers {
  handleNameChange: (val: string) => void
  handleDescriptionChange: (val: string) => void
  handleVariantChange: (val: string) => void
  handleUnitChange: (val: string) => void
  handlePackWeightKgChange: (val: string) => void
  handleStockChange: (val: string) => void
  handleManufacturerPriceChange: (val: string) => void
  handleMoqChange: (val: string) => void
  handleMrpChange: (val: string) => void
  handleSellingPriceChange: (val: string) => void
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  handleRemoveImage: (index: number) => void
  handleSubmit: (e: React.SyntheticEvent<HTMLFormElement>) => Promise<void>
}

interface FormSectionProps {
  formState: SkuFormState
  handlers: SkuFormHandlers
}

const useSkuModalForm = ({ isOpen, onClose, onSubmit, item = null }: SkuFormModalProps) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [images, setImages] = useState<string[]>([])
  const [moq, setMoq] = useState<string>('')
  const [mrp, setMrp] = useState<string>('')
  const [sellingPrice, setSellingPrice] = useState<string>('')
  const [manufacturerPrice, setManufacturerPrice] = useState<string>('')
  const [stock, setStock] = useState<string>('')
  const [variant, setVariant] = useState<string>('')
  const [unit, setUnit] = useState<string>('Bag')
  const [packWeightKg, setPackWeightKg] = useState<string>('')

  const [submitting, setSubmitting] = useState(false)
  const [uploadingImages, setUploadingImages] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const init = getInitialFormState(item)
    setName(init.name)
    setDescription(init.description)
    setImages(init.images)
    setMoq(init.moq)
    setMrp(init.mrp)
    setSellingPrice(init.sellingPrice)
    setManufacturerPrice(init.manufacturerPrice)
    setStock(init.stock)
    setVariant(init.variant)
    setUnit(init.unit)
    setPackWeightKg(init.packWeightKg)
    setErrorMsg('')
    setErrors({})
  }, [item, isOpen])

  const clearFieldError = (key: string) => {
    setErrors((prev) => clearKey(prev, key))
  }

  const handleNameChange = (val: string) => {
    setName(val)
    clearFieldError('name')
  }

  const handleDescriptionChange = (val: string) => {
    setDescription(val)
    clearFieldError('description')
  }

  const handleVariantChange = (val: string) => {
    setVariant(val)
    clearFieldError('variant')
  }

  const handleUnitChange = (val: string) => {
    setUnit(val)
    if (val === 'Kg') {
      setPackWeightKg('')
      clearFieldError('packWeightKg')
    }
  }

  const handlePackWeightKgChange = (val: string) => {
    setPackWeightKg(val)
    clearFieldError('packWeightKg')
  }

  const handleStockChange = (val: string) => {
    setStock(val)
    clearFieldError('stock')
  }

  const handleManufacturerPriceChange = (val: string) => {
    setManufacturerPrice(val)
    clearFieldError('manufacturerPrice')
  }

  const handleMoqChange = (val: string) => {
    setMoq(val)
    clearFieldError('moq')
  }

  const handleMrpChange = (val: string) => {
    setMrp(val)
    setErrors((prev) => updateErrorsForMrp(val, sellingPrice, prev))
  }

  const handleSellingPriceChange = (val: string) => {
    setSellingPrice(val)
    setErrors((prev) => updateErrorsForSellingPrice(val, mrp, prev))
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const slots = 5 - images.length
    if (!files || files.length === 0 || slots <= 0) return

    setUploadingImages(true)
    setErrorMsg('')

    try {
      const uploadedUrls = await uploadCatalogFiles(files, slots)
      const updatedImages = [...images, ...uploadedUrls]
      setImages(updatedImages)
      clearFieldError('images')
    } catch {
      setErrorMsg('Failed to upload one or more images. Please try again.')
    } finally {
      setUploadingImages(false)
      e.target.value = ''
    }
  }

  const handleRemoveImage = (index: number) => {
    const nextImages = images.filter((_, idx) => idx !== index)
    setImages(nextImages)
    if (nextImages.length === 0) {
      setErrors((prev) => ({ ...prev, images: 'At least one product image is required.' }))
    }
  }

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    const newErrors = validateSkuForm({
      name,
      description,
      images,
      mrp,
      sellingPrice,
      manufacturerPrice,
      moq,
      variant,
      packWeightKg,
      stock,
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSubmitting(true)
    setErrorMsg('')
    setErrors({})

    try {
      await onSubmit({
        name: name.trim(),
        variant: variant.trim(),
        description: description.trim(),
        images,
        moq: Number(moq) || 0,
        stock: Number(stock) || 0,
        mrp: Number(mrp),
        sellingPrice: Number(sellingPrice),
        manufacturerPrice: Number(manufacturerPrice),
        unit,
        packWeightKg: packWeightKg.trim() ? Number(packWeightKg) : undefined,
      })
      onClose()
    } catch {
      setErrorMsg('Failed to save SKU. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return {
    formState: {
      name,
      description,
      images,
      moq,
      mrp,
      sellingPrice,
      manufacturerPrice,
      stock,
      variant,
      unit,
      packWeightKg,
      submitting,
      uploadingImages,
      errorMsg,
      errors,
    },
    handlers: {
      handleNameChange,
      handleDescriptionChange,
      handleVariantChange,
      handleUnitChange,
      handlePackWeightKgChange,
      handleStockChange,
      handleManufacturerPriceChange,
      handleMoqChange,
      handleMrpChange,
      handleSellingPriceChange,
      handleFileChange,
      handleRemoveImage,
      handleSubmit,
    },
  }
}

const ProductIdentitySection = ({ formState, handlers }: FormSectionProps) => {
  const { name, description, variant, unit, packWeightKg, errors } = formState

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <span className="h-4 w-1 bg-primary rounded-full" />
        <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">Product Identity</h4>
      </div>

      {/* Product Name */}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
          Product Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => handlers.handleNameChange(e.target.value)}
          placeholder="e.g. MilkMax Lactation Mash"
          className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
            errors.name ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
          }`}
        />
        {errors.name && <span className="text-xs text-danger font-medium mt-1 block">{errors.name}</span>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
          Description <span className="text-danger">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => handlers.handleDescriptionChange(e.target.value)}
          placeholder="Describe product details, feed properties, yield benefits..."
          rows={3}
          className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
            errors.description ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
          }`}
        />
        {errors.description && <span className="text-xs text-danger font-medium mt-1 block">{errors.description}</span>}
      </div>

      {/* Size / Packaging */}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
          Size / Packaging <span className="text-danger">*</span>
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <input
            type="text"
            value={variant}
            onChange={(e) => handlers.handleVariantChange(e.target.value)}
            placeholder="e.g. 50kg Bag, 25kg Bag, 1 Liter Bottle"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.variant ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          <select
            value={unit}
            onChange={(e) => handlers.handleUnitChange(e.target.value)}
            className="w-full sm:w-32 px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-primary focus:outline-none transition-colors"
          >
            {UNIT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        {errors.variant && <span className="text-xs text-danger font-medium mt-1 block">{errors.variant}</span>}
      </div>

      {/* Pack Weight */}
      {unit !== 'Kg' && (
        <div className="space-y-1.5">
          <label className="text-xs text-text-secondary block">
            Pack weight in kg (used for MT/volume reporting — leave blank for non-weight units)
          </label>
          <input
            type="number"
            min="0"
            step="any"
            value={packWeightKg}
            onKeyDown={handlePriceKeyDown}
            onPaste={(e) => handlePaste(e, sanitizePrice, handlers.handlePackWeightKgChange)}
            onChange={(e) => handlers.handlePackWeightKgChange(sanitizePrice(e.target.value))}
            placeholder="e.g. 50"
            className={`w-full sm:w-64 px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.packWeightKg ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.packWeightKg && (
            <span className="text-xs text-danger font-medium mt-1 block">{errors.packWeightKg}</span>
          )}
        </div>
      )}
    </div>
  )
}

const ProductGallerySection = ({ formState, handlers }: FormSectionProps) => {
  const { images, uploadingImages, errors } = formState

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <span className="h-4 w-1 bg-primary rounded-full" />
        <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">Product Gallery</h4>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
          Images <span className="text-danger">*</span>{' '}
          <span className="text-[10px] text-text-secondary font-normal normal-case">
            (PNG, JPG, JPEG, WEBP — Max 5, Min 1 required)
          </span>
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {images.map((imgUrl, index) => (
            <div
              key={imgUrl}
              className="relative group rounded-card overflow-hidden h-20 border border-border bg-bg/20 aspect-square w-full"
            >
              <img src={imgUrl} className="w-full h-full object-cover" alt="Preview" />
              <button
                type="button"
                onClick={() => handlers.handleRemoveImage(index)}
                className="absolute inset-0 bg-danger/80 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-xs font-bold"
              >
                Remove
              </button>
            </div>
          ))}
          {images.length < 5 && (
            <label
              className={`border-2 border-dashed rounded-card flex flex-col items-center justify-center h-20 bg-bg/10 transition-all duration-200 aspect-square w-full ${
                uploadingImages
                  ? 'border-border opacity-50 cursor-not-allowed'
                  : 'border-border hover:border-primary hover:bg-primary/5 cursor-pointer active:scale-95'
              }`}
            >
              {uploadingImages ? (
                <svg className="animate-spin h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                </svg>
              )}
              <span className="text-[10px] text-text-secondary font-bold mt-1">
                {uploadingImages ? 'Uploading...' : 'Upload'}
              </span>
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/webp"
                multiple
                className="hidden"
                onChange={handlers.handleFileChange}
                disabled={uploadingImages}
              />
            </label>
          )}
        </div>
        <p className="text-[10px] text-text-secondary">Supported formats: PNG, JPG, JPEG, WEBP</p>
        {errors.images && <span className="text-xs text-danger font-medium mt-1 block">{errors.images}</span>}
      </div>
    </div>
  )
}

const PricingStockSection = ({ formState, handlers }: FormSectionProps) => {
  const { mrp, sellingPrice, manufacturerPrice, moq, stock, errors } = formState

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <span className="h-4 w-1 bg-primary rounded-full" />
        <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">Pricing & Stock</h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        {/* MRP */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
            MRP <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            min="0.01"
            step="any"
            value={mrp}
            onKeyDown={handlePriceKeyDown}
            onPaste={(e) => handlePaste(e, sanitizePrice, handlers.handleMrpChange)}
            onChange={(e) => handlers.handleMrpChange(sanitizePrice(e.target.value))}
            placeholder="0"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.mrp ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.mrp && <span className="text-xs text-danger font-medium mt-1 block">{errors.mrp}</span>}
        </div>

        {/* Selling Price */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
            Selling Price <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            min="0.01"
            step="any"
            value={sellingPrice}
            max={mrp && Number(mrp) > 0 ? mrp : undefined}
            onKeyDown={handlePriceKeyDown}
            onPaste={(e) => handlePaste(e, sanitizePrice, handlers.handleSellingPriceChange)}
            onChange={(e) => handlers.handleSellingPriceChange(sanitizePrice(e.target.value))}
            placeholder="0"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.sellingPrice ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.sellingPrice && (
            <span className="text-xs text-danger font-medium mt-1 block">{errors.sellingPrice}</span>
          )}
        </div>

        {/* Manufacturer Price */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
            Manufacturer Price <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            min="0"
            step="any"
            value={manufacturerPrice}
            onKeyDown={handlePriceKeyDown}
            onPaste={(e) => handlePaste(e, sanitizePrice, handlers.handleManufacturerPriceChange)}
            onChange={(e) => handlers.handleManufacturerPriceChange(sanitizePrice(e.target.value))}
            placeholder="0"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.manufacturerPrice ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.manufacturerPrice && (
            <span className="text-xs text-danger font-medium mt-1 block">{errors.manufacturerPrice}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        {/* MOQ */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">Min Order Qty</label>
          <input
            type="number"
            min="0"
            step="1"
            value={moq}
            onKeyDown={handleIntegerKeyDown}
            onPaste={(e) => handlePaste(e, sanitizeInteger, handlers.handleMoqChange)}
            onChange={(e) => handlers.handleMoqChange(sanitizeInteger(e.target.value))}
            placeholder="0"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.moq ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.moq && <span className="text-xs text-danger font-medium mt-1 block">{errors.moq}</span>}
        </div>

        {/* Stock */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
            Overall Inventory Stock <span className="text-text-secondary font-normal normal-case">(Optional)</span>
          </label>
          <input
            type="number"
            min="0"
            step="1"
            value={stock}
            onKeyDown={handleIntegerKeyDown}
            onPaste={(e) => handlePaste(e, sanitizeInteger, handlers.handleStockChange)}
            onChange={(e) => handlers.handleStockChange(sanitizeInteger(e.target.value))}
            placeholder="0"
            className={`w-full px-3.5 py-2 border rounded-btn bg-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all ${
              errors.stock ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            }`}
          />
          {errors.stock && <span className="text-xs text-danger font-medium mt-1 block">{errors.stock}</span>}
        </div>
      </div>
    </div>
  )
}

export function SkuFormModal({ isOpen, onClose, onSubmit, item = null }: SkuFormModalProps) {
  const { formState, handlers } = useSkuModalForm({ isOpen, onClose, onSubmit, item })
  const { submitting, uploadingImages, errorMsg } = formState

  const submitButtonLabel = item ? 'Save SKU' : 'Add SKU'

  const footerActions = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={submitting}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="sku-form"
        disabled={submitting || uploadingImages}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors disabled:opacity-50 min-w-[5rem] flex items-center justify-center"
      >
        {submitting ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          submitButtonLabel
        )}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={item ? 'Edit Catalog SKU Item' : 'Add New Catalog SKU'}
      footer={footerActions}
      size="md"
    >
      <form id="sku-form" onSubmit={handlers.handleSubmit} className="space-y-6 pb-4">
        {errorMsg && (
          <div className="p-3.5 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-semibold flex items-center gap-2">
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {errorMsg}
          </div>
        )}

        <ProductIdentitySection formState={formState} handlers={handlers} />
        <ProductGallerySection formState={formState} handlers={handlers} />
        <PricingStockSection formState={formState} handlers={handlers} />
      </form>
    </Modal>
  )
}
